<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Time Sheet" class="px-4 pr-5 mb-5">
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-2 pb-3"
      >
        <v-col cols="12" sm="5" md="4" lg="3">
          <WeekPicker
            :date="dateFilter"
            :hideDetails="true"
            @weekChange="weekChangeHandler"
            @dateChange="(newValue) => this.dateFilter = newValue"
          ></WeekPicker>
        </v-col>
        <v-col cols="12" sm="7" md="5" lg="6" xl="4">
          <v-select
            label="Contract"
            v-model="selectedAssignmentId"
            :items="assignmentsOptionsList"
            item-text="text"
            item-value="value"
            outlined
            hide-details
            @change="selectedAssignmentChangeHandler"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3" xl="2" style="color: rgba(0,0,0,.6);" class="text-center" v-if="!showNoDataMessage">
          <v-chip
            color="primary"
            class="py-5"
          >
            Total:&nbsp;&nbsp;{{ parseFloat(weekTotalHours).toFixed(2) }} Hours
          </v-chip>
        </v-col>
      </v-row>
    </div>
    <v-container fluid class="px-0 pt-2">
      <v-card style="max-height: 590px; overflow-y: auto; overflow-x: hidden;" v-if="!showNoDataMessage">
        <v-card-text>
          <v-row>
            <v-col
              class="px-1 time-sheet-day-size"
              v-for="entry in timeSheetEntries" :key="entry.date"
            >
              <ConsultantTimeSheetEntry
                :entry="entry"
                @entryTimeChange="entryTimeChangeHandler"
              ></ConsultantTimeSheetEntry>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <div v-if="showNoDataMessage">
        <WithoutAssignmentEntriesView
        ></WithoutAssignmentEntriesView>
      </div>
    </v-container>
    <ButtonsSubmitForm
      v-if="currentOrUpcomingWeek && !showNoDataMessage"
      :isFormValid="true"
      acceptLabel="Save"
      cancelLabel="Discard"
      @save="saveTimeSheetEntriesHandler"
      @cancel="discardChangesHandler"
      >
    </ButtonsSubmitForm>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer, ButtonsSubmitForm } from '@/components/buttons'
import { WeekPicker } from '@/components/shared/datePickers'
import { getCurrentUser } from '@/lib/authentication'
import { nextTick } from 'vue'
import NoDataAvailableMessage from '@/components/reports/NoDataAvailableMessage'
import WithoutAssignmentEntriesView from './WithoutAssignmentEntriesView'
import ConsultantTimeSheetEntry from './ConsultantTimeSheetEntry'
import { formatDate } from '@/lib/display'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    WeekPicker,
    NoDataAvailableMessage,
    WithoutAssignmentEntriesView,
    ConsultantTimeSheetEntry,
    ButtonsSubmitForm
  },
  data () {
    return {
      currentWeekDateRange: null,
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      assignmentsOptionsList: [],
      selectedAssignmentId: null,
      showNoDataMessage: true,
      weekTotalHours: 0,
      tempTimeSheetEntriesToSave: [],
      currentUser: null,
      timeSheetEntries: []
    }
  },
  computed: {
    ...mapFields('consultantTimeSheet', [
      'consultantTimeSheetEntries'
    ]),
    currentOrUpcomingWeek () {
      if (this.currentWeekDateRange && this.dateBeginFilter) {
        return new Date(this.dateBeginFilter) >= new Date(this.currentWeekDateRange.beginDate)
      }
      return false
    }
  },
  watch: {
    timeSheetEntries () {
      if (this.timeSheetEntries && this.timeSheetEntries.length > 0) {
        this.weekTotalHours = this.timeSheetEntries.reduce((acc, row) => {
          return acc + row.hours
        }, 0)
      } else {
        this.weekTotalHours = 0
      }
      this.showNoDataMessage = !(this.timeSheetEntries && this.timeSheetEntries.length > 0)
    }
  },
  methods: {
    ...mapActions('assignments', [
      'getAssignmentsByUserId'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime',
      'getStaffLynxCurrentWeekDateRange'
    ]),
    ...mapActions('consultantTimeSheet', [
      'getTimeSheetEntries',
      'saveTimeSheetEntries'
    ]),
    ...mapActions('staffTimeSheet', [
      'getStaffTimeSheetEntries',
      'saveStaffTimeSheetEntries'
    ]),
    weekChangeHandler (dateBegin, dateEnd) {
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      this.loadTimeSheetEntries()
    },
    selectedAssignmentChangeHandler () {
      this.loadTimeSheetEntries()
    },
    entryTimeChangeHandler (entry, newHoursValue) {
      if (entry.hours !== newHoursValue) {
        let timeSheetEntryResult = this.tempTimeSheetEntriesToSave.filter(tse =>
          tse.staffId === entry.staffId &&
          tse.assignmentId === entry.assignmentId &&
          tse.date === entry.date
        )
        if (timeSheetEntryResult && timeSheetEntryResult.length > 0) {
          timeSheetEntryResult[0].hours = newHoursValue
        } else {
          entry.hours = newHoursValue
          this.tempTimeSheetEntriesToSave.push(entry)
        }
      }
    },
    saveTimeSheetEntriesHandler () {
      if (this.tempTimeSheetEntriesToSave && this.tempTimeSheetEntriesToSave.length > 0) {
        if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
          this.saveStaffTimeSheetEntries(this.tempTimeSheetEntriesToSave).then(() => {
            this.loadTimeSheetEntries()
          })
        } else {
          this.saveTimeSheetEntries(this.tempTimeSheetEntriesToSave).then(() => {
            this.loadTimeSheetEntries()
          })
        }
      }
    },
    discardChangesHandler () {
      if (this.tempTimeSheetEntriesToSave && this.tempTimeSheetEntriesToSave.length > 0) {
        this.loadTimeSheetEntries()
      }
    },
    async loadTimeSheetEntries () {
      if (this.selectedAssignmentId && this.dateBeginFilter && this.dateEndFilter) {
        if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
          this.timeSheetEntries = await this.getStaffTimeSheetEntries({
            staffId: this.selectedAssignmentId.substring(1),
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          })
        } else {
          this.timeSheetEntries = await this.getTimeSheetEntries({
            assignmentId: this.selectedAssignmentId,
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          })
        }
      }
      this.tempTimeSheetEntriesToSave = []
    }
  },
  async created () {
    this.currentUser = await getCurrentUser()
    // Add the Internal Employee assignment if staffEmployeeId different from null
    if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
      // Add a S to avoid repeating the same Id of an assignment.
      this.assignmentsOptionsList.push({
        value: 'S' + this.currentUser.staffEmployeeId,
        text: 'Internal Employee - ' + formatDate(this.currentUser.staffEmployeeStartDate)
      })
      this.selectedAssignmentId = 'S' + this.currentUser.staffEmployeeId
    }
    this.currentWeekDateRange = await this.getStaffLynxCurrentWeekDateRange()
    let assignmentsList = await this.getAssignmentsByUserId(getCurrentUser().id)
    assignmentsList = assignmentsList.filter(a => a.timeSheetType === 2)
    this.showNoDataMessage = !(assignmentsList && assignmentsList.length > 0)
    if (!this.showNoDataMessage) {
      this.selectedAssignmentId = assignmentsList[0].id
      this.assignmentsOptionsList = assignmentsList.map(assignment => {
        return {
          value: assignment.id,
          text: assignment.clientName + ' - ' + assignment.jobTitle + ' - ' + formatDate(assignment.startAt)
        }
      })
      await nextTick()
      if (this.selectedAssignmentId) {
        this.loadTimeSheetEntries()
      }
    }
  }
}
</script>
<style>
  @media only screen and (max-width: 600px) {
    .time-sheet-day-size {
      min-width: 50% !important;
      max-width: 50% !important;
    }
  }

  @media only screen and (min-width: 601px) and (max-width: 768px) {
    .time-sheet-day-size {
      min-width: 25% !important;
      max-width: 25% !important;
    }
  }

  @media only screen and (min-width: 769px) {
    .time-sheet-day-size {
      min-width: 14.28% !important;
      max-width: 14.28% !important;
    }
  }
</style>
